import { useForm, Controller, useFieldArray } from "react-hook-form";
import React, { useState, useEffect, useRef } from "react";
import Stepper from "../../common/utils/Stepper";
import FormInput from "../../common/components/FormInput";
import FormFileInput from "../../common/components/FormFileInput";
import FormSelect from "../../common/components/FormSelect";
import CustomDatePicker from "../../common/components/DatePicker";
import {
  validateEmail,
  validateMobileNumber,
  validatePincode,
  validateFileSize,
  validatePolicyorClaimNumber,
  validateBankDetails,
  validateRequired,
} from "../../common/utils/validation";
import { formConfig } from "../../constants";
import "./ClaimIntimation.scss";

const FILE_ALLOWED_TYPES = ["image/jpeg", "image/png", "application/pdf"];
const FILE_MAX_SIZE_MB = 10;

const getBaseFieldName = (fieldName) =>
  fieldName.includes(".") ? fieldName.split(".").pop() : fieldName;

const getFieldValidationRules = (fieldName, fieldConfig = {}) => {
  if (fieldConfig.component === "file") {
    return {
      validate: (value) => {
        if (!value) {
          return fieldConfig.required ? "This document is required" : true;
        }
        return (
          validateFileSize(value, FILE_MAX_SIZE_MB, FILE_ALLOWED_TYPES) || true
        );
      },
    };
  }

  const baseName = getBaseFieldName(fieldName);

  const rulesByField = {
    policyNo: {
      validate: (value) =>
        validatePolicyorClaimNumber(value, "policy number") || true,
    },
    otherPolicyNo: {
      validate: (value) =>
        validatePolicyorClaimNumber(value, "policy number") || true,
    },
    claimantMail: {
      validate: (value) => validateEmail(value) || true,
    },
    mobileNo: {
      validate: (value) => validateMobileNumber(value) || true,
    },
    pincode: {
      validate: (value) => validatePincode(value) || true,
    },
    accountNumber: {
      validate: (value) => validateBankDetails(value, "Account Number") || true,
    },
    bankName: {
      validate: (value) => validateBankDetails(value, "Bank Name") || true,
    },
    ifscCode: {
      validate: (value) => validateBankDetails(value, "IFSC Code") || true,
    },
    mcrCode: {
      validate: (value) => validateBankDetails(value, "MICR Code") || true,
    },
    branchName: {
      validate: (value) => validateBankDetails(value, "Branch Name") || true,
    },
    nameOfLifeAssured: {
      validate: (value) =>
        validateRequired(value, "Name of Life Assured") || true,
    },
    claimantName: {
      validate: (value) => validateRequired(value, "Claimant Name") || true,
    },
  };

  return rulesByField[baseName] ?? {};
};

const stepLabels = formConfig.map(({ label }) => label);

const getStepConfig = (step) => formConfig.find((item) => item.step === step);

const getFieldsForStep = (step) => {
  const config = getStepConfig(step);
  if (!config) return [];

  if (config.sections) {
    return config.sections.flatMap((section) =>
      section.fields.map((field) => field.name),
    );
  }

  return config.fields?.map((field) => field.name) ?? [];
};

const getFieldArrayDefaultItem = (fields = []) =>
  fields.reduce((acc, { name, component }) => {
    acc[name] = component === "date" ? null : "";
    return acc;
  }, {});

const RenderField = ({
  fieldConfig,
  control,
  errors,
  handleChange,
  toTitleCase,
  errorKey,
}) => {
  const {
    name,
    label,
    component,
    options,
    type,
    placeholder,
    required,
    rules,
    minDate,
    maxDate,
    maxLength,
    onChangeType,
    transform,
    disabledWhenFilled,
  } = fieldConfig;

  const getNestedError = (obj, path) =>
    path.split(".").reduce((acc, key) => acc?.[key], obj)?.message;

  const error = errorKey
    ? getNestedError(errors, errorKey)
    : errors?.[name]?.message;

  return (
    <Controller
      name={name}
      control={control}
      rules={{
        ...rules,
        ...getFieldValidationRules(name, fieldConfig),
      }}
      render={({ field }) => {
        const disabled = disabledWhenFilled ? !!field.value : false;

        switch (component) {
          case "input":
            return (
              <FormInput
                {...field}
                name={name}
                label={label}
                type={type || "text"}
                placeholder={placeholder}
                required={required}
                disabled={disabled}
                maxLength={maxLength}
                value={field.value ?? ""}
                error={error}
                onChange={(e) => {
                  if (transform === "titleCase") {
                    field.onChange(toTitleCase(e.target.value));
                    return;
                  }
                  if (onChangeType) {
                    handleChange(e, field.onChange);
                    return;
                  }
                  field.onChange(e.target.value);
                }}
              />
            );

          case "select":
            return (
              <FormSelect
                {...field}
                label={label}
                options={options}
                required={required}
                error={error}
              />
            );

          case "date":
            return (
              <CustomDatePicker
                name={name}
                label={label}
                required={required}
                value={field.value}
                minDate={minDate}
                maxDate={maxDate}
                disabled={disabled}
                onBlur={field.onBlur}
                onChange={field.onChange}
                error={error}
              />
            );

          case "file":
            return (
              <FormFileInput
                label={label}
                required={required}
                onChange={(e) => field.onChange(e.target.files[0])}
                error={error}
                value={field.value}
              />
            );

          default:
            return null;
        }
      }}
    />
  );
};

const StepFormContent = ({
  currentStep,
  control,
  errors,
  handleChange,
  toTitleCase,
  selectedRelation,
  policyFields,
  onAppendPolicy,
  onRemovePolicy,
}) => {
  const [activePolicyTab, setActivePolicyTab] = useState(0);
  const prevPolicyCountRef = useRef(policyFields.length);

  const stepConfig = getStepConfig(currentStep);

  useEffect(() => {
    if (!stepConfig?.fieldArray) {
      setActivePolicyTab(0);
    }
  }, [currentStep, stepConfig?.fieldArray]);

  useEffect(() => {
    const len = policyFields.length;
    const prev = prevPolicyCountRef.current;
    if (len > prev && len > 1) {
      setActivePolicyTab(len - 1);
    }
    prevPolicyCountRef.current = len;
  }, [policyFields.length]);

  if (!stepConfig) return null;

  const fieldProps = {
    control,
    errors,
    handleChange,
    toTitleCase,
  };

  const handleRemovePolicyAt = (index) => {
    const len = policyFields.length;
    onRemovePolicy(index);
    setActivePolicyTab((prev) => {
      const newLen = len - 1;
      if (newLen <= 1) return 0;
      if (prev < index) return prev;
      if (prev > index) return prev - 1;
      return Math.min(index, newLen - 1);
    });
  };

  if (stepConfig.fieldArray) {
    const useTabs = policyFields.length > 1;
    const fieldArrayName = stepConfig.fieldArray;

    const renderPolicyFields = (index, item) => (
      <div className="claim-intimation__form">
        {stepConfig.fields.map((fieldConfig) => (
          <RenderField
            key={`${item.id}-${fieldConfig.name}`}
            fieldConfig={{
              ...fieldConfig,
              name: `${fieldArrayName}.${index}.${fieldConfig.name}`,
            }}
            {...fieldProps}
            errorKey={`${fieldArrayName}.${index}.${fieldConfig.name}`}
          />
        ))}
      </div>
    );

    return (
      <div
        key={currentStep}
        className={`claim-intimation__form-content${useTabs ? " claim-intimation__form-content--policy-tabs" : ""}`}
      >
        {stepConfig.title && (
          <div className="claim-intimation__header">
            <h4>{stepConfig.title}</h4>
          </div>
        )}

<>
            <div
              className="claim-intimation__policy-tabs"
              role="tablist"
              aria-label="Other policies"
            >
              <div className="claim-intimation__policy-tabs-list">
                {policyFields.map((item, index) => (
                  <button
                    key={item.id}
                    type="button"
                    role="tab"
                    id={`policy-tab-${index}`}
                    aria-selected={activePolicyTab === index}
                    aria-controls={`policy-panel-${index}`}
                    tabIndex={activePolicyTab === index ? 0 : -1}
                    className={`claim-intimation__policy-tab ${activePolicyTab === index ? " claim-intimation__policy-tab--active" : ""}`}
                    onClick={() => setActivePolicyTab(index)}
                  >
                    Policy {index + 1}
                    {item.length > 1 &&
                        <span
                        className="claim-intimation__policy-tab-remove"
                        onClick={() => handleRemovePolicyAt(index)}
                        >
                        <svg
                            width="13"
                            height="14"
                            viewBox="0 0 13 14"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                            d="M11.75 2.5H9.5V1.75C9.5 1.28587 9.31563 0.840752 8.98744 0.512563C8.65925 0.184374 8.21413 0 7.75 0H4.75C4.28587 0 3.84075 0.184374 3.51256 0.512563C3.18437 0.840752 3 1.28587 3 1.75V2.5H0.75C0.551088 2.5 0.360322 2.57902 0.21967 2.71967C0.0790177 2.86032 0 3.05109 0 3.25C0 3.44891 0.0790177 3.63968 0.21967 3.78033C0.360322 3.92098 0.551088 4 0.75 4H1V12.5C1 12.8315 1.1317 13.1495 1.36612 13.3839C1.60054 13.6183 1.91848 13.75 2.25 13.75H10.25C10.5815 13.75 10.8995 13.6183 11.1339 13.3839C11.3683 13.1495 11.5 12.8315 11.5 12.5V4H11.75C11.9489 4 12.1397 3.92098 12.2803 3.78033C12.421 3.63968 12.5 3.44891 12.5 3.25C12.5 3.05109 12.421 2.86032 12.2803 2.71967C12.1397 2.57902 11.9489 2.5 11.75 2.5ZM4.5 1.75C4.5 1.6837 4.52634 1.62011 4.57322 1.57322C4.62011 1.52634 4.6837 1.5 4.75 1.5H7.75C7.8163 1.5 7.87989 1.52634 7.92678 1.57322C7.97366 1.62011 8 1.6837 8 1.75V2.5H4.5V1.75ZM10 12.25H2.5V4H10V12.25ZM5.5 6V10C5.5 10.1989 5.42098 10.3897 5.28033 10.5303C5.13968 10.671 4.94891 10.75 4.75 10.75C4.55109 10.75 4.36032 10.671 4.21967 10.5303C4.07902 10.3897 4 10.1989 4 10V6C4 5.80109 4.07902 5.61032 4.21967 5.46967C4.36032 5.32902 4.55109 5.25 4.75 5.25C4.94891 5.25 5.13968 5.32902 5.28033 5.46967C5.42098 5.61032 5.5 5.80109 5.5 6ZM8.5 6V10C8.5 10.1989 8.42098 10.3897 8.28033 10.5303C8.13968 10.671 7.94891 10.75 7.75 10.75C7.55109 10.75 7.36032 10.671 7.21967 10.5303C7.07902 10.3897 7 10.1989 7 10V6C7 5.80109 7.07902 5.61032 7.21967 5.46967C7.36032 5.32902 7.55109 5.25 7.75 5.25C7.94891 5.25 8.13968 5.32902 8.28033 5.46967C8.42098 5.61032 8.5 5.80109 8.5 6Z"
                            fill="#DA1414"
                            ></path>
                        </svg>
                        </span>
                    }
                  </button>
                ))}
              </div>
              <button
                type="button"
                className="claim-intimation__policy-tab-add btn tertiary-button regular"
                onClick={onAppendPolicy}
              >
                + Add More
              </button>
            </div>

            <div
              role="tabpanel"
              id={`policy-panel-${activePolicyTab}`}
              aria-labelledby={`policy-tab-${activePolicyTab}`}
              className="claim-intimation__policy-tab-panel"
            >
              {renderPolicyFields(
                activePolicyTab,
                policyFields[activePolicyTab],
              )}
            </div>
          </>
      </div>
    );
  }

  if (stepConfig.sections) {
    return (
      <div key={currentStep} className="claim-intimation__form-content">
        {stepConfig.sections.map((section) => (
          <div key={section.title} className="claim-intimation__form-fields">
            <div className="claim-intimation__header">
              <h4>{section.title}</h4>
            </div>
            <div className="claim-intimation__form">
              {section.fields.map((field) => (
                <RenderField
                  key={field.name}
                  fieldConfig={field}
                  {...fieldProps}
                />
              ))}
              {section.fields.some(
                (f) => f.name === "relationWithLifeAssurance",
              ) &&
                selectedRelation === "Others" && (
                  <Controller
                    name="otherRelation"
                    control={control}
                    rules={{ required: "Please specify relation" }}
                    render={({ field }) => (
                      <FormInput
                        label="Please specify relation"
                        required
                        {...field}
                        error={errors.otherRelation?.message}
                      />
                    )}
                  />
                )}
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div key={currentStep} className="claim-intimation__form-content">
      <div className="claim-intimation__form-fields">
        <div className="claim-intimation__header">
          {" "}
          {stepConfig.title && <h4>{stepConfig.title}</h4>}{" "}
          {stepConfig.description && (
            <p className="claim-intimation__description">
              {stepConfig.description}
            </p>
          )}{" "}
        </div>
        <div className="claim-intimation__form">
          {stepConfig.fields?.map((field) => (
            <RenderField key={field.name} fieldConfig={field} {...fieldProps} />
          ))}
        </div>
      </div>
      {stepConfig.showConsent && (
        <div className="claim-intimation__form-consent">
          <Controller
            name="consent"
            control={control}
            render={({ field }) => (
              <label className="terms checkbox">
                <input type="checkbox" {...field} checked={field.value} />
                <span>
                  By submitting this form, you consent to us verifying and using
                  the information provided.
                </span>
              </label>
            )}
          />
          {errors.consent && (
            <p className="error-text">{errors.consent.message}</p>
          )}
        </div>
      )}
    </div>
  );
};

const StepActions = ({
  currentStep,
  onPrev,
  onNext,
  onSkip,
  onDraft,
  consentChecked,
}) => {
  const stepConfig = getStepConfig(currentStep);

  if (!stepConfig?.buttons?.length) return null;

  const actionHandlers = {
    prev: onPrev,
    next: onNext,
    skip: onSkip,
    draft: onDraft,
  };

  const buttonClassName = {
    prev: "secondary-button",
    next: "primary-button",
    skip: "secondary-button",
    draft: "secondary-button",
  };

  const renderButton = (button, index) => {
    const isSubmit = button.type === "submit";
    const handler = button.action && actionHandlers[button.action];
    const disabled =
      button.disabledWhen === "noConsent" ? !consentChecked : false;

    return (
      <button
        key={`${button.label}-${index}`}
        type={button.type}
        disabled={disabled}
        onClick={!isSubmit && handler ? handler : undefined}
        className={`form-button btn  ${buttonClassName[button.action]} regular`}
      >
        {button.label}
      </button>
    );
  };

  return (
    <div className={`form-actions-wrapper ${stepConfig.buttons.length > 1 ? "form-actions-wrapper--multiple" : ""}`}>
      {stepConfig.buttons.map((group, groupIndex) => (
        <div key={groupIndex} className="form-actions-group">
          {group.map((button, index) => renderButton(button, index))}
        </div>
      ))}
    </div>
  );
};

const ClaimIntimationForm = () => {
  const {
    handleSubmit,
    trigger,
    control,
    watch,
    formState: { errors },
  } = useForm({
    mode: "onBlur",
    defaultValues: {
      policyNo: "",
      nameOfLifeAssured: "",
      dob: null,
      deathDate: null,
      claimantName: "",
      claimantMail: "",
      mobileNo: "",
      address: "",
      pincode: "",
      accountNumber: "",
      bankName: "",
      ifscCode: "",
      mcrCode: "",
      branchName: "",
      relationWithLifeAssurance: "",
      country: "",
      state: "",
      deathCause: "",
      deathReason: "",
      consent: false,
      policies: [
        {
          otherPolicyNo: "",
          commencementDate: null,
          sumAssured: "",
          icName: "",
          claimStatus: "",
        },
      ],
    },
  });

  const selectedRelation = watch("relationWithLifeAssurance");
  const consentChecked = watch("consent");

  const { fields, append, remove } = useFieldArray({
    control,
    name: "policies",
  });

  const [currentStep, setCurrentStep] = useState(0);

  const handleChange = (e, onChange) => {
    const { name, value } = e.target;

    if (
      name === "policyNo" ||
      name === "claimRefNo" ||
      (name.startsWith("policies.") && name.endsWith(".otherPolicyNo"))
    ) {
      if (!/^\d*$/.test(value)) return;
      if (value.length > 8) return;
    }
    if (name === "mobileNo") {
      if (!/^\d*$/.test(value)) return;
    }
    if (name === "pincode") {
      if (!/^\d*$/.test(value)) return;
      if (value.length > 6) return;
    }
    onChange(value);
  };

  const toTitleCase = (value = "") =>
    value.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());

  const skipStep = () => setCurrentStep((s) => s + 1);

  const stepConfig = getStepConfig(currentStep);

  const handleAppendPolicy = () => {
    append(getFieldArrayDefaultItem(getStepConfig(3)?.fields));
  };

  const nextStep = async () => {
    if (stepConfig?.fieldArray) {
      const valid = await trigger(stepConfig.fieldArray);
      if (!valid) return;
      setCurrentStep((s) => s + 1);
      return;
    }

    let fieldsToValidate = getFieldsForStep(currentStep);

    if (stepConfig?.sections && selectedRelation === "Others") {
      fieldsToValidate = [...fieldsToValidate, "otherRelation"];
    }

    const valid = await trigger(fieldsToValidate);
    if (!valid) return;

    setCurrentStep((s) => s + 1);
  };

  const prevStep = () => setCurrentStep((s) => s - 1);

  const onSubmit = (_data) => {
    // Form submitted with data
  };

  return (
    <div className="claim-intimation-wrapper">
      <Stepper steps={stepLabels} currentStep={currentStep} />
      <div className="claim-intimation">
        <form
          className="claim-intimation__card"
          onSubmit={handleSubmit(onSubmit)}
        >
          <StepFormContent
            currentStep={currentStep}
            control={control}
            errors={errors}
            handleChange={handleChange}
            toTitleCase={toTitleCase}
            selectedRelation={selectedRelation}
            policyFields={fields}
            onAppendPolicy={handleAppendPolicy}
            onRemovePolicy={remove}
          />

          <StepActions
            currentStep={currentStep}
            onPrev={prevStep}
            onNext={nextStep}
            onSkip={skipStep}
            onDraft={() => {}}
            consentChecked={consentChecked}
          />
        </form>
      </div>
    </div>
  );
};

export default ClaimIntimationForm;
